import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { settingsApi } from '../api';
import { useAuth } from './AuthContext';
import { useClinicEvent } from './SocketContext';

const ClinicContext = createContext(null);

const CACHE_KEY = 'shivam_clinic_details';
const FALLBACK = { clinicName: '', tagline: '', logoUrl: '' };

/**
 * The name is remembered between visits so the navbar and login screen paint it
 * immediately instead of flashing a placeholder while the request is in flight.
 */
function readCache() {
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY));
    return cached?.clinicName ? { ...FALLBACK, ...cached } : FALLBACK;
  } catch {
    return FALLBACK;
  }
}

/**
 * Holds the clinic's own details so the navbar, the login screen and the
 * prescription all read the name from one place. Refreshes by itself when
 * settings are saved on either computer.
 */
export function ClinicProvider({ children }) {
  const { user } = useAuth();
  const [clinic, setClinic] = useState(readCache);

  const load = useCallback(async () => {
    try {
      // Signed-in users get the full settings; the login screen only needs the name.
      const res = user ? await settingsApi.get() : await settingsApi.public();
      const next = { ...FALLBACK, ...res.data };
      setClinic(next);

      try {
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({ clinicName: next.clinicName, tagline: next.tagline, logoUrl: next.logoUrl }),
        );
      } catch {
        // a full or blocked localStorage is not worth failing over
      }
    } catch {
      // keep whatever is on screen; a stale name beats a blank one
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  useClinicEvent('settings:changed', load);

  return <ClinicContext.Provider value={{ clinic, reloadClinic: load }}>{children}</ClinicContext.Provider>;
}

export function useClinic() {
  const ctx = useContext(ClinicContext);
  if (!ctx) throw new Error('useClinic must be used inside <ClinicProvider>');
  return ctx;
}
